/**
 * `KeyChord` — a normalized key + modifier set (#64, modifiers live in #68).
 *
 * The chord carries modifiers (ctrl/alt/shift/meta). `chordId` folds them into
 * the reverse-index key, so `Shift+1` and `1` are DISTINCT bindings (#68).
 *
 * Normalization makes chord equality TOTAL, hashable, and PORTABLE:
 *   • `key` is lower-cased for single printable characters ("S" → "s") so
 *     Shift-produced capitals match their base key; named keys ("Tab",
 *     "Escape", "ArrowUp") are preserved verbatim.
 *   • modifier set has a fixed field order and boolean-only values.
 *   • CROSS-PLATFORM: the "primary" command modifier is `ctrl || meta`, so a
 *     macOS `Cmd+1` and a Windows/Linux `Ctrl+1` resolve to the SAME chord.
 *     This keeps a keymap portable across OSes (and, later, account-synced
 *     keymaps that roam between machines). We store the raw flags faithfully
 *     but canonicalize at `chordId`/label time.
 */

/** Modifier flags on a chord. Fixed field set; boolean-only. */
export interface ModifierSet {
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly shift: boolean;
  readonly meta: boolean;
}

/** A normalized keyboard chord: a key plus its modifier set. */
export interface KeyChord {
  /** Normalized `KeyboardEvent.key` (lower-cased for single chars). */
  readonly key: string;
  /** Modifier flags. Stored + typed; NOT consulted by v1 matching (#68). */
  readonly mods: ModifierSet;
}

/** All-false modifier set — the common (no-modifier) case. */
export const NO_MODS: ModifierSet = {
  ctrl: false,
  alt: false,
  shift: false,
  meta: false,
};

/**
 * Normalize a raw key + optional modifier flags into a canonical `KeyChord`.
 * Single printable characters are lower-cased ("S" → "s", "A" → "a") so a
 * Shift-produced capital matches its base binding; multi-char named keys
 * ("Tab", "Escape") pass through unchanged. Pure.
 */
export function normalizeChord(key: string, mods?: Partial<ModifierSet>): KeyChord {
  const normKey = key.length === 1 ? key.toLowerCase() : key;
  return {
    key: normKey,
    mods: {
      ctrl: mods?.ctrl ?? false,
      alt: mods?.alt ?? false,
      shift: mods?.shift ?? false,
      meta: mods?.meta ?? false,
    },
  };
}

/**
 * Canonical modifier prefix for a chord's id/label. Order is FIXED
 * (primary, then alt, then shift) so equality is total. `primary` collapses
 * ctrl/meta cross-platform (see file header). Returns e.g. "", "p+", "p+s+",
 * "s+" — a stable, hashable prefix.
 */
function modifierPrefix(mods: ModifierSet): string {
  const primary = mods.ctrl || mods.meta;
  let prefix = "";
  if (primary) prefix += "p+";
  if (mods.alt) prefix += "a+";
  if (mods.shift) prefix += "s+";
  return prefix;
}

/**
 * The reverse-index key for a chord: canonical modifier prefix + normalized
 * key. `Shift+1` → "s+1", `Ctrl+1`/`Cmd+1` → "p+1", bare `1` → "1". Distinct
 * modifier combos are distinct ids; cross-platform primary is unified. This is
 * the single choke point for chord matching.
 */
export function chordId(chord: KeyChord): string {
  return modifierPrefix(chord.mods) + chord.key;
}

/**
 * Human-readable chord label for the UI, e.g. "Ctrl+Shift+1", "Alt+Q", "Tab".
 * Uses the same canonical ordering as `chordId`; `primary` renders as "Ctrl"
 * (the common cross-platform name). A bare key renders as just the key.
 */
export function chordLabel(chord: KeyChord): string {
  const parts: string[] = [];
  if (chord.mods.ctrl || chord.mods.meta) parts.push("Ctrl");
  if (chord.mods.alt) parts.push("Alt");
  if (chord.mods.shift) parts.push("Shift");
  const key = chord.key === " " ? "Space" : displayKey(chord.key);
  parts.push(key);
  return parts.join("+");
}

/** Title-case a single-char key for display ("q" → "Q"); named keys verbatim. */
function displayKey(key: string): string {
  return key.length === 1 ? key.toUpperCase() : key;
}

/** Convenience: build a bare (no-modifier) chord from a raw key. */
export function bareChord(key: string): KeyChord {
  return normalizeChord(key, undefined);
}
